import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { PageScrollView } from 'components/Page';
import { useEsc } from 'hooks/useEsc';
import React from 'react';
import { useDispatch } from 'react-redux';
import { NAVIGATE_MODAL_BACK } from 'store/actions';
import {
  doSetupApiCreateWebhook,
  doSetupApiDeleteWebhook,
  doSetupApiTestWebhook,
  doSetupApiUpdateWebhook,
} from 'store/remote/actions';
import { TextField } from 'components/Field/TextField';
import { str } from 'lib/str';
import { Button, ButtonTheme } from 'components/Button';
import { SeparatorHorizontal } from 'components/SeparatorHorizontal';
import { formatDate, numberToDate } from 'lib/riskbase';
import { UI_DATE_FORMAT_LONG, UI_TIME_FORMAT } from 'lib/constants';
import { theme } from 'lib/theme';
import { ICONS, Icon } from 'components/Icon';
import { View } from 'react-native';
import { Txt } from 'components/Txt';

export const ApiWebhookModal: React.FC<
  ModalScreenProps<ModalScreens.SettingsApiWebhook>
> = ({ route }) => {
  const dispatch = useDispatch();
  const { type, data } = route.params;
  const [changeSecret, setChangeSecret] = React.useState(type === 'new');
  const secretRef = React.useRef('');
  const urlRef = React.useRef(
    type === 'existing'
      ? data.url.startsWith('https://')
        ? data.url.replace('https://', '')
        : data.url
      : '',
  );
  const getCanSubmit = () => !!urlRef.current.trim();
  const [canSubmit, setCanSubmit] = React.useState(getCanSubmit());
  const [testWait, setTestWait] = React.useState(false);
  const updateCanSubmit = () => setCanSubmit(getCanSubmit());

  const handleChangeUrl = (newUrl: string) => {
    const cleanUrl = newUrl.startsWith('https://')
      ? newUrl.replace('https://', '')
      : newUrl;
    urlRef.current = cleanUrl;
    updateCanSubmit();
  };

  const handleChangeSecret = (newSecret: string) => {
    secretRef.current = newSecret;
  };

  const handleTestWebhook = () => {
    dispatch(
      doSetupApiTestWebhook({
        organisationId: data.organisationId,
        id: type === 'existing' ? data.id : null,
        url: urlRef.current,
        secret: changeSecret ? secretRef.current : null,
      }),
    );
    setTestWait(true);
  };

  const handleSave = () => {
    if (type === 'existing') {
      dispatch(
        doSetupApiUpdateWebhook({
          id: data.id,
          organisationId: data.organisationId,
          url: urlRef.current,
          secret: changeSecret ? secretRef.current : null,
        }),
      );
      return;
    }
    dispatch(
      doSetupApiCreateWebhook({
        organisationId: data.organisationId,
        url: urlRef.current,
        secret: secretRef.current,
      }),
    );
  };

  React.useEffect(() => {
    const waitTimeout = testWait
      ? setTimeout(() => {
          setTestWait(false);
        }, 5000)
      : undefined;
    return () => {
      if (waitTimeout) {
        clearTimeout(waitTimeout);
      }
    };
  }, [testWait]);

  const handleClose = React.useCallback(() => {
    dispatch(NAVIGATE_MODAL_BACK());
  }, [dispatch]);
  useEsc(handleClose);

  const SECRET_DESCRIPTION =
    `${
      type === 'existing'
        ? data.hasSecret
          ? 'You can update or remove your secret but be aware that any integrations using this secret will need to be updated.\n\n'
          : `If you add a secret, make sure you remember it because we${str.apos}ll never show it to you again.\n\n`
        : `Make sure you remember your secret because we${str.apos}ll never show it to you again.\n\n`
    }` +
    'Secrets are optional but best practice. If present, RiskBase will create a hash signature with your secret and include it as a ' +
    'X-RB-Signature-256 header in all webhook requests. Use the hash signature to validate webhook deliveries.';

  const errorString =
    type === 'existing'
      ? {
          6: 'Host Not Resolved',
          28: 'Timed Out',
        }[data.lastRequestErrorNumber || -1]
      : undefined;

  return (
    <PageScrollView
      header={
        <NavigationHeader
          heading={type === 'existing' ? 'API Webhook' : 'Create API Webhook'}
          cancelButtonText="Close"
          onPressCancelButton={handleClose}
          onPressSaveButton={canSubmit ? handleSave : null}
        />
      }
    >
      <Card
        explanation={
          `We${str.apos}ll send a POST request to this URL for each event. ` +
          'Double check your URL is correct after saving this webhook'
        }
      >
        <TextField
          onChange={handleChangeUrl}
          placeholder="URL*"
          value={urlRef.current}
          autoFocus
          autoFocusDelay
          onEsc={handleClose}
          autoCorrect={false}
          autoCapitalize="none"
          textLeft="https://"
          inputStyle={{ paddingLeft: 1 }}
        />
      </Card>

      {type === 'existing' &&
        (changeSecret ? (
          <Card
            label="Secret"
            explanation={SECRET_DESCRIPTION}
            labelLinkText="Cancel Change"
            onLabelLinkPress={() => {
              setChangeSecret(false);
              secretRef.current = '';
            }}
          >
            <TextField
              onChange={handleChangeSecret}
              placeholder="Leave blank for no secret"
              maxLength={512}
            />
          </Card>
        ) : (
          <Card
            label="Secret"
            explanation={SECRET_DESCRIPTION}
            labelLinkText="Update Secret"
            onLabelLinkPress={() => setChangeSecret(true)}
          >
            <CardRow
              value={data.hasSecret ? 'Secret hidden...' : ''}
              placeholder={!data.hasSecret ? 'No secret set' : ''}
            />
          </Card>
        ))}

      {type === 'new' && (
        <Card label="Secret" explanation={SECRET_DESCRIPTION}>
          <TextField
            onChange={handleChangeSecret}
            placeholder="Leave blank for no secret"
            maxLength={512}
          />
        </Card>
      )}

      <View>
        <Card margin={false}>
          <CardRow
            label="Test Webhook"
            value='[{"type":"test","id":randomId}]'
            onPress={handleTestWebhook}
            icon={ICONS.GearComplexCodeLight}
            labelAlignTop
            valueStyle={{ fontFamily: 'Courier New' }}
            valueColor={theme.colors.blue}
            labelColor={theme.colors.blue}
            iconColor={theme.colors.blue}
          />
        </Card>
        {testWait && (
          <View
            style={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: theme.colors.background80,
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'row',
            }}
          >
            <Icon
              source={ICONS.ClockLight}
              style={{ margin: theme.paddingHorizontal }}
            />
            <Txt>Please wait to test again...</Txt>
          </View>
        )}
      </View>

      {type === 'existing' && (
        <>
          <SeparatorHorizontal marginVertical={theme.paddingVertical} />
          <Card marginBottomHalf>
            <CardRow
              label="Last Ran"
              value={
                data.lastRequestTs
                  ? `${formatDate(
                      numberToDate(data.lastRequestTs),
                      UI_DATE_FORMAT_LONG,
                    )} ${formatDate(
                      numberToDate(data.lastRequestTs),
                      UI_TIME_FORMAT,
                    )}`
                  : 'Never'
              }
            />
            {data.lastRequestStatus ? (
              <CardRow
                label="Last Status"
                value={data.lastRequestStatus.toString()}
                valueColor={
                  data.lastRequestStatus.toString().startsWith('2')
                    ? theme.colors.green
                    : theme.colors.red
                }
              />
            ) : errorString ? (
              <CardRow
                label="Last Status"
                value={errorString}
                valueColor={theme.colors.red}
              />
            ) : null}
          </Card>
          <Card margin={false}>
            <CardRow
              label="Created"
              value={`${formatDate(
                numberToDate(data.createdTs),
                UI_DATE_FORMAT_LONG,
              )} ${formatDate(numberToDate(data.createdTs), UI_TIME_FORMAT)}`}
            />
          </Card>
          <SeparatorHorizontal marginVertical={theme.paddingVertical} />
          <Button
            text="Delete Webhook"
            destructive
            buttonTheme={ButtonTheme.themeless}
            onPress={() =>
              dispatch(
                doSetupApiDeleteWebhook({
                  id: data.id,
                  organisationId: data.organisationId,
                  url: data.url,
                }),
              )
            }
            style={{ alignSelf: 'center' }}
          />
        </>
      )}
    </PageScrollView>
  );
};
